/* import __COLOCATED_TEMPLATE__ from './fin-readiness-table.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';

import type Store from '@ember-data/store';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';

interface Args {
  reportState: ReportState;
}

export default class FinReadinessTable extends Component<Args> {
  @service declare appService: AppService;
  @service declare store: Store;

  @use counterResource = staticDataResource(this, {
    serializedChart: () => this.informationalConversationsChartDefinition,
    reportState: () => this.args.reportState,
  });

  @use convWithQueryTypeResource = staticDataResource(this, {
    serializedChart: () => this.convWithQueryTypeChartDefinition,
    reportState: () => this.args.reportState,
  });

  @use timeSavedResource = staticDataResource(this, {
    serializedChart: () => this.timeSavedChartDefinition,
    reportState: () => this.args.reportState,
  });

  get informationalPersonalizedActionsConversationsFilter() {
    return {
      type: 'category',
      data: {
        property: 'conversation_custom_fields#ai_insights_query_type',
        attribute: 'conversation_custom_fields#ai_insights_query_type',
        values: ['informational', 'personalized', 'actions'],
      },
    };
  }

  get queryTypeFilter() {
    return {
      type: 'exists',
      data: {
        property: 'conversation_custom_fields#ai_insights_query_type',
        attribute: 'conversation_custom_fields#ai_insights_query_type',
      },
    };
  }

  get informationalConversationsChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'and',
            filters: [this.informationalPersonalizedActionsConversationsFilter],
          },
        },
      ],
    };
  }

  get convWithQueryTypeChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'and',
            filters: [this.queryTypeFilter],
          },
        },
      ],
    };
  }

  get timeSavedChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.handling_time',
          aggregation: 'sum',
          filters: {
            type: 'and',
            filters: [this.informationalPersonalizedActionsConversationsFilter],
          },
        },
      ],
    };
  }

  get informationalConversationsValue() {
    return this.counterResource.formatValue({
      value: this.counterResource.kpiData[0],
      metricId: this.informationalConversationsChartDefinition.chart_series[0].metric_id,
    });
  }

  get convWithQueryTypeValue() {
    return this.convWithQueryTypeResource.formatValue({
      value: this.convWithQueryTypeResource.kpiData[0],
      metricId: this.convWithQueryTypeChartDefinition.chart_series[0].metric_id,
    });
  }

  get timeSavedValue() {
    return this.timeSavedResource.formatValue({
      value: this.timeSavedResource.kpiData[0],
      metricId: this.timeSavedChartDefinition.chart_series[0].metric_id,
    });
  }

  get informationalPercentage() {
    let informationalConversationsValue = this.counterResource.kpiData[0] as number;
    let convValue = this.convWithQueryTypeResource.kpiData[0] as number;
    return `${((informationalConversationsValue / convValue) * 100).toFixed(0)}%`;
  }

  get isLoading() {
    return (
      this.counterResource.isLoading ||
      this.timeSavedResource.isLoading ||
      this.convWithQueryTypeResource.isLoading
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::FinReadiness::FinReadinessTable': typeof FinReadinessTable;
  }
}
